import { Button } from '@/components/shadcn/button'
import { DataTable } from '@/components/ui/DataTable'
import { RestSchemas } from '@/contexts/QueryProvider'
import { SectionContainer } from '@/pages/shared-components/SectionContainer'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import { useMemo } from 'react'
import { UserTokenLimitForm } from '../Forms/UserTokenLimitForm'
import { type TokenLimitOverrideRow } from './TokenLimitOverrideRow'
import { getTokenLimitOverrideColumns } from './TokenLimitOverridesTable/tokenLimitOverridesTableColumns'

type OrganisationUser = RestSchemas['Messages.Subscriptions.OrganisationUser']
type UserTokenLimitEntry = RestSchemas['Messages.Subscriptions.UserTokenLimitEntry']

export function TokenLimitOverridesSection({
  userLimits,
  organisationUsers,
  canEdit,
}: {
  userLimits: UserTokenLimitEntry[]
  organisationUsers: OrganisationUser[]
  canEdit: boolean
}) {
  const usersById = useMemo(() => new Map(organisationUsers.map((user) => [user.id, user])), [organisationUsers])

  const rows = useMemo<TokenLimitOverrideRow[]>(
    () =>
      userLimits.flatMap((entry) => {
        if (entry.userId == null || entry.limitUsd == null) return []
        const user = usersById.get(entry.userId)
        return [
          {
            userId: entry.userId,
            limitUsd: entry.limitUsd,
            givenName: user?.givenName ?? '',
            familyName: user?.familyName ?? '',
            email: user?.email ?? `User ${entry.userId}`,
          },
        ]
      }),
    [userLimits, usersById]
  )

  const existingOverrideUserIds = useMemo(() => rows.map((row) => row.userId), [rows])

  const columns = useMemo(
    () =>
      getTokenLimitOverrideColumns({
        canEdit,
        organisationUsers,
        existingOverrideUserIds,
      }),
    [canEdit, organisationUsers, existingOverrideUserIds]
  )

  return (
    <SectionContainer
      title="Overrides"
      description="Users listed here have a daily USD cap different from the organisation default."
    >
      <div className="h-[32rem] min-h-[16rem]">
        <DataTable
          columns={columns}
          data={rows}
          action={
            canEdit ? (
              <UserTokenLimitForm
                organisationUsers={organisationUsers}
                existingOverrideUserIds={existingOverrideUserIds}
                sheetTitle="Add override"
                sheetDescription="Set a daily token limit in USD for a user. This replaces the organisation default for that user only."
                sheetTrigger={
                  <Button size="lg" variant="outline">
                    <IconPlusOutline className="mr-2" />
                    Add override
                  </Button>
                }
              />
            ) : undefined
          }
        />
      </div>
    </SectionContainer>
  )
}
